import axios from 'axios';
import Environment from '../../utils/Environment';
import { unwrapResponse } from './apiResponse';

export interface UserRole {
    roleId: string;
    roleName: string;
    dcCode: string;
    capabilities: string[];
}

export interface UserPermission {
    userId: string;
    preferredDc?: string;
    roles: UserRole[];
}

const buildPermission = (data: any): UserPermission => {
    return {
        userId: data.userId,
        preferredDc: data.preferredDc || undefined,
        roles: data.roles || [],
    };
};

// Flatten capabilities from all roles for a permission
export const getCapabilities = (permission: UserPermission): string[] => {
    return permission.roles.flatMap(role => role.capabilities || []);
};

export const getUserPermissions = async (userId: string): Promise<UserPermission> => {
    const baseURL = Environment.getHubUserPermissionsAPI();
    const url = `${baseURL}/${encodeURIComponent(userId)}`;
    console.log('Fetching user permissions from API:', url);
    const response = await axios.get(url);
    const data = unwrapResponse<any>(response.data);
    console.log('User permissions response:', data);
    return buildPermission(data);
};


export const savePreferredDC = async (userId: string, dcCode: string): Promise<void> => {
    const baseURL = Environment.getHubUserPreferencesAPI();
    const url = `${baseURL}/${encodeURIComponent(userId)}`;
    const now = new Date().toISOString();
    await axios.post(url, {
        userId,
        dstCntrCd: dcCode,
        createTs: now,
        createUserId: userId,
        lastUpdTs: now,
        lastUpdUserId: userId,
    });
};

// Lowercases and strips spaces/underscores/hyphens so capability keys written in code
// (e.g. 'camsrouting', 'self_service') still match differently formatted API values
// (e.g. 'CAMS Routing', 'SELF SERVICE').
export const normalizeCapabilityKey = (value: string): string =>
    value.toLowerCase().replace(/[^a-z0-9]/g, '');

// capability: short key like 'xdock', 'in02', 'routing'
// Matches if any role's capability string contains the key once both are normalized
// e.g. 'in02' matches 'IN02 Shipment planning', 'camsrouting' matches 'CAMS Routing'
export const hasCapability = (permission: UserPermission, capability: string): boolean => {
    const target = normalizeCapabilityKey(capability);
    return getCapabilities(permission).some(cap => normalizeCapabilityKey(cap).includes(target));
};
